import { useEffect, useState } from 'react';
import axios from 'axios';

const POLL_INTERVAL_MS = 60_000;

/**
 * Polls the governance approval queue for its pending count, so the
 * Settings gear icon can show a badge instead of requiring someone to
 * remember to open Governance -> Approval Queue.
 *
 * Checks the admin-configurable badge_enabled toggle (Settings ->
 * Notifications) first; the count is only fetched/polled when that toggle
 * is on, so someone who turns the badge off doesn't keep paying for the
 * extra poll every 60s.
 *
 * Silently returns 0 on any fetch error (network hiccup, no permission,
 * endpoint unavailable) -- a missing badge is a minor UX gap, not
 * something that should ever surface as a visible error to the user.
 */
export function usePendingApprovalCount(enabled: boolean): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const fetchCount = async () => {
      try {
        const response = await axios.get<{ count?: number }>(
          '/api/governance/approvals',
          { params: { status: 'pending' } }
        );
        if (!cancelled) {
          setCount(response.data.count ?? 0);
        }
      } catch {
        if (!cancelled) {
          setCount(0);
        }
      }
    };

    const start = async () => {
      try {
        const badgeResponse = await axios.get<{ enabled?: boolean }>(
          '/api/notifications/badge-enabled'
        );
        if (cancelled || badgeResponse.data.enabled === false) {
          return;
        }
      } catch {
        // Fails safe to "enabled" -- see hook-level doc comment.
      }

      await fetchCount();
      intervalId = setInterval(fetchCount, POLL_INTERVAL_MS);
    };

    start();

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [enabled]);

  return count;
}
